import { useMemo } from "react";

import { Link, Route, Routes, useLocation } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Menu } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import AssessmentsDashboard from "./AssessmentsDashboard";
import AssignmentDashboard from "./Assignment/AssignmentDashboard/AssignmentDashboard";
import YourAssessments from "./YourAssessments";

type IChallengeAssessments = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  demoingTour?: string;
};

const ChallengeAssessments = ({
  challenge,
  demoingTour,
}: IChallengeAssessments) => {
  const user = useAppSelector((state) => state.user);
  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );
  const { pathname } = useLocation();
  const challengeId = challenge?._id;

  const assignedAssessmentQuery = useQuery(
    initUseQuery(api.challenges.getAssignedAssessments, [challengeId], {
      isEnabled: !!user,
    }),
  );
  const myAssignedAssessments = assignedAssessmentQuery.data?.ideas || [];

  return (
    <div>
      {canManageChallenge ||
      util.hasPermission(user, "challenge.viewAssessments", challengeId) ? (
        <Menu pointing secondary>
          <Menu.Item
            content="Dashboard"
            as={Link}
            to="."
            active={pathname === `/challenges/${challengeId}/assessments`}
          />
          {canManageChallenge ||
          util.hasPermission(user, "challenge.editAssessments", challengeId) ? (
            <Menu.Item
              content="Assignment"
              as={Link}
              to="assignment"
              active={
                pathname === `/challenges/${challengeId}/assessments/assignment`
              }
            />
          ) : null}
          {myAssignedAssessments?.length ? (
            <Menu.Item
              as={Link}
              to="assigned"
              active={
                pathname === `/challenges/${challengeId}/assessments/assigned`
              }
            >
              Your assessments
            </Menu.Item>
          ) : null}
        </Menu>
      ) : null}
      <Routes>
        <Route
          path="/"
          element={
            canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.viewAssessments",
              challengeId,
            ) ? (
              <AssessmentsDashboard challenge={challenge} />
            ) : (
              <YourAssessments
                challenge={challenge}
                demoingTour={demoingTour}
              />
            )
          }
        />
        {canManageChallenge ||
        util.hasPermission(user, "challenge.editAssessments", challengeId) ? (
          <Route
            path="assignment"
            element={<AssignmentDashboard challenge={challenge} />}
          />
        ) : null}
        <Route
          path="assigned"
          element={
            <YourAssessments challenge={challenge} demoingTour={demoingTour} />
          }
        />
      </Routes>
    </div>
  );
};

export default ChallengeAssessments;
